<template>
  <div class="withdraw">
    <el-row :gutter="20">
      <!-- 发起提现 -->
      <el-col :xs="24" :md="14">
        <el-card shadow="never">
          <div slot="header">
            <h4><i class="el-icon-bank-card" style="color:#3a8ee6"></i> 发起提现申请</h4>
          </div>
          <el-form ref="withdrawForm" :model="withdrawForm" :rules="withdrawRules" label-width="0">
            <el-form-item prop="amount">
              <div class="amount-input-box">
                <span class="amount-prefix">&yen;</span>
                <el-input-number v-model="withdrawForm.amount" :precision="2" :min="100" :step="100" controls-position="right" style="width:100%" placeholder="0.00" />
              </div>
              <p class="amount-tip">当前可用余额 &yen; {{ formatMoney(balance) }}，单次提现至少 100 元且为 100 的整数倍</p>
            </el-form-item>
            <el-form-item label="到账账户" label-width="80px">
              <div class="bank-card" @click="openBankCardDialog" style="width:100%">
                <div class="bank-info">
                  <i class="el-icon-bank-card bank-icon"></i>
                  <div>
                    <p class="bank-name">{{ bankCardInfo.bank_name || '未设置银行卡' }}</p>
                    <p class="bank-holder">
                      <template v-if="bankCardInfo.bank_card_no">
                        {{ bankCardInfo.bank_name || '--' }} (尾号 {{ bankCardInfo.bank_card_no.slice(-4) }})
                      </template>
                      <template v-else>
                        点击设置银行卡
                      </template>
                    </p>
                  </div>
                </div>
                <i class="el-icon-arrow-right"></i>
              </div>
            </el-form-item>
            <el-form-item>
              <el-button type="primary" style="width:100%;height:48px;font-size:16px" @click="submitWithdraw" :disabled="!bankCardInfo.bank_card_no">提交提现申请</el-button>
            </el-form-item>
          </el-form>
        </el-card>
      </el-col>

      <!-- 规则提示 -->
      <el-col :xs="24" :md="10">
        <el-card shadow="never" class="rule-card">
          <div slot="header">
            <h4><i class="el-icon-warning" style="color:#e6a23c"></i> 提现规则提示</h4>
          </div>
          <ul class="rule-list">
            <li><strong>最低额度：</strong>账户余额满 100 元才可发起提现。</li>
            <li><strong>提取金额：</strong>仅支持 100 元整数倍提取（如 100, 500, 1200 等）。</li>
            <li><strong>手续费用：</strong>每笔提现将扣除 10% 的系统服务手续费。</li>
            <li><strong>处理时间：</strong>提现申请于每周一人工统一核实处理。</li>
            <li><strong>到账周期：</strong>审核通过后，资金将在 3 个工作日内到达您的绑定账户。</li>
          </ul>
        </el-card>
      </el-col>
    </el-row>

    <!-- 提现记录 -->
    <el-card shadow="never" style="margin-top:20px">
      <div slot="header">
        <span>提现记录</span>
      </div>
      <el-table :data="withdrawList" border stripe v-loading="loading">
        <el-table-column prop="id" label="申请编号" width="100" />
        <el-table-column label="提现金额" width="120">
          <template slot-scope="scope">&yen; {{ scope.row.amount }}</template>
        </el-table-column>
        <el-table-column label="手续费" width="100">
          <template slot-scope="scope">&yen; {{ scope.row.fee || 0 }}</template>
        </el-table-column>
        <el-table-column label="实际到账" width="120">
          <template slot-scope="scope">&yen; {{ ((scope.row.amount || 0) - (scope.row.fee || 0)).toFixed(2) }}</template>
        </el-table-column>
        <el-table-column prop="create_time" label="申请时间" width="150" />
        <el-table-column label="状态" width="100">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === 'approved' ? 'success' : scope.row.status === 'rejected' ? 'danger' : 'warning'" size="small">
              {{ scope.row.status === 'approved' ? '已通过' : scope.row.status === 'rejected' ? '已驳回' : '审核中' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" min-width="140" show-overflow-tooltip />
      </el-table>
      <div class="pagination-box">
        <el-pagination background layout="total, prev, pager, next" :total="total" :page-size="10" :current-page="page" @current-change="onPageChange" />
      </div>
    </el-card>

    <!-- 银行卡设置弹窗 -->
    <el-dialog title="银行卡设置" :visible.sync="bankCardDialogVisible" width="480px" @closed="loadBankCard">
      <el-form ref="bankForm" :model="bankForm" :rules="bankRules" label-width="90px">
        <el-form-item label="开户银行" prop="bank_name">
          <el-select v-model="bankForm.bank_name" placeholder="请选择开户银行" style="width:100%">
            <el-option v-for="b in bankList" :key="b" :label="b" :value="b" />
          </el-select>
        </el-form-item>
        <el-form-item label="银行卡号" prop="bank_card_no">
          <el-input v-model="bankForm.bank_card_no" placeholder="请输入银行卡号" maxlength="19" />
        </el-form-item>
        <el-form-item label="持卡人姓名" prop="bank_card_holder">
          <el-input v-model="bankForm.bank_card_holder" placeholder="请输入持卡人姓名" maxlength="20" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="bankCardDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitBankCard">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getWithdraw, applyWithdraw, getWithdrawList, saveBankCard } from '@/api'

export default {
  name: 'Withdraw',
  data() {
    return {
      balance: 0,
      withdrawForm: { amount: 100 },
      withdrawRules: {
        amount: [
          { required: true, message: '请输入提现金额', trigger: 'blur' },
          { type: 'number', min: 100, message: '最低提现100元', trigger: 'blur' }
        ]
      },
      withdrawList: [], loading: false, total: 0, page: 1,
      // 银行卡
      bankCardInfo: { bank_name: '', bank_card_no: '', bank_card_holder: '' },
      bankCardDialogVisible: false,
      bankForm: { bank_name: '', bank_card_no: '', bank_card_holder: '' },
      bankRules: {
        bank_name: [{ required: true, message: '请选择开户银行', trigger: 'change' }],
        bank_card_no: [
          { required: true, message: '请输入银行卡号', trigger: 'blur' },
          { pattern: /^\d{16,19}$/, message: '银行卡号格式不正确', trigger: 'blur' }
        ],
        bank_card_holder: [{ required: true, message: '请输入持卡人姓名', trigger: 'blur' }]
      },
      bankList: [
        '中国工商银行', '中国农业银行', '中国银行', '中国建设银行', '交通银行',
        '招商银行', '中国邮政储蓄银行', '浦发银行', '兴业银行', '中信银行',
        '中国民生银行', '光大银行', '平安银行', '华夏银行', '广发银行',
        '北京银行', '上海银行', '江苏银行', '南京银行', '宁波银行'
      ]
    }
  },
  mounted() {
    this.loadBalance()
    this.loadList()
  },
  methods: {
    async loadBalance() {
      try {
        const res = await getWithdraw()
        this.balance = res.balance || 0
        this.bankCardInfo = {
          bank_name: res.bank_name || '',
          bank_card_no: res.bank_card_no || '',
          bank_card_holder: res.bank_card_holder || ''
        }
      } catch (e) {}
    },
    async loadList() {
      this.loading = true
      try {
        const res = await getWithdrawList({ page: this.page, limit: 10 })
        this.withdrawList = res.data || res || []
        this.total = res.total || 0
      } catch (e) {}
      this.loading = false
    },
    onPageChange(p) { this.page = p; this.loadList(); },
    openBankCardDialog() {
      this.bankForm = { ...this.bankCardInfo }
      this.bankCardDialogVisible = true
    },
    async loadBankCard() {
      // 弹窗关闭后重新加载
      await this.loadBalance()
    },
    async submitBankCard() {
      try {
        await this.$refs.bankForm.validate()
      } catch (e) { return }
      try {
        await saveBankCard(this.bankForm)
        this.$message.success('银行卡信息已保存')
        this.bankCardDialogVisible = false
        this.loadBalance()
      } catch (e) {
        this.$message.error(e.message || '保存失败')
      }
    },
    submitWithdraw() {
      if (!this.bankCardInfo.bank_card_no) {
        this.$message.warning('请先设置银行卡信息')
        return
      }
      this.$refs.withdrawForm.validate(async valid => {
        if (!valid) return
        const amt = this.withdrawForm.amount
        if (amt % 100 !== 0) { this.$message.warning('提现金额需为100的整数倍'); return }
        if (amt > this.balance) { this.$message.warning('余额不足'); return }
        try {
          await applyWithdraw({ amount: amt })
          this.$message.success('申请已提交，请等待审核')
          this.withdrawForm.amount = 100
          this.loadList()
          this.loadBalance()
        } catch (e) {
          this.$message.error(e.message || '提交失败')
        }
      })
    },
    formatMoney(val) {
      if (!val && val !== 0) return '--'
      return Number(val).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }
  }
}
</script>

<style scoped>
.amount-input-box { position: relative; display: flex; align-items: center; }
.amount-prefix { position: absolute; left: 12px; font-size: 24px; font-weight: bold; color: #c0c4cc; z-index: 1; }
.amount-input-box >>> .el-input-number .el-input__inner { padding-left: 40px; height: 56px; font-size: 28px; font-weight: bold; color: #3a8ee6; }
.amount-tip { font-size: 12px; color: #c0c4cc; margin-top: 8px; }
.bank-card { display: flex; justify-content: space-between; align-items: center; padding: 16px; border: 1px solid #e4e7ed; border-radius: 8px; background: #fafafa; width: 100%; cursor: pointer; transition: border-color .2s; }
.bank-card:hover { border-color: #3a8ee6; }
.bank-info { display: flex; align-items: center; gap: 12px; }
.bank-icon { font-size: 28px; color: #409eff; }
.bank-name { font-weight: bold; font-size: 14px; margin: 0; }
.bank-holder { font-size: 12px; color: #c0c4cc; margin: 2px 0 0; }
.rule-card { background: #fef0e6; border: 1px solid #fde2c8; }
.rule-list { margin: 0; padding-left: 20px; }
.rule-list li { margin-bottom: 10px; font-size: 13px; color: #e6a23c; line-height: 1.6; }
.pagination-box { text-align: right; margin-top: 16px; }
</style>
